import {
    afterNextRender,
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Inject,
    inject,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import SourceCategoriesService from '@app/services/source-categories.service';
import ButtonComponent from '@app/shared/button/button.component';
import DropdownComponent from '@app/shared/common/dropdown/dropdown.component';
import { IDropdownOption } from '@app/shared/common/dropdown/DropdownOption';
import FormSectionComponent from '@app/shared/common/form-section/form-section.component';
import InputComponent from '@app/shared/input/input.component';
import SvgIconComponent from '@app/shared/svg-icon/svg-icon.component';
import AppPathConsts from '@shared/AppPathConsts';
import AppPermissions from '@shared/AppPermissionNames';
import AppSettings from '@shared/AppSettings';
import AppComponentBase from '@shared/common/app-component-base';
import { ISearchInterestSurveyAction, ISourceCategoryDropdownOption, ISourceSelection, nullable } from '@shared/common/common-types';
import { SelectedAwareOptionsPipe } from '@shared/common/pipes/selected-aware-options.pipe';
import SmallCompanyLogoPipe from '@shared/common/pipes/small-company-logo.pipe';
import {
    ClientCrossCompanyActivityResponseModel,
    ClientMatchDetailsResponseModel,
    ClientMatchSourceCategoryResponseModel,
    ClientRequestModel,
    ClientsCrossCompanyServiceProxy,
    ClientSourceCategoryRequestModel,
    ClientSourceDetailRequestModel,
    ClientSourcesRequestModel,
    ClientsServiceProxy,
    ContactDetailId,
    ContactDetailsExistenceResponseModel,
    NationalitiesServiceProxy,
    NomenclaturesPaginatedRequestModel,
    OfferOfClientResponseModel,
    OffersServiceProxy,
    ProjectOfClientResponseModel,
    ProjectsServiceProxy,
    SearchesServiceProxy,
    SearchOfClientResponseModel,
} from '@shared/service-proxies/service-proxies';
import ClientsService from '@shared/utils/clients.service';
import { isEmailSearchTerm, sanitizeContactSearchTerm } from '@shared/utils/contact-search.utils';
import { defaultDropdownMapper, getSourceDetailName } from '@shared/utils/dropdown.utils';
import { normalizePhoneNumber } from '@shared/utils/phone.utils';
import { SanitizeContactSearchDirective } from '@shared/utils/sanitize-contact-search.directive';
import UtilsModule from '@shared/utils/utils.module';
import { clientFirstNameValidator } from '@shared/utils/validation/client-firstName-validator';
import { first, isEmpty, isNil } from 'lodash';
import { catchError, EMPTY, finalize, forkJoin, Observable, of, switchMap } from 'rxjs';

import TelInputComponent from '../../../../shared/common/tel-input/tel-input.component';

import ClientOffersSearchesProjectsComponent from './client-offers-searches-projects/client-offers-searches-projects.component';

@Component({
    selector: 'app-client-add-popup-form',
    imports: [
        SelectedAwareOptionsPipe,
        InputComponent,
        FormsModule,
        ReactiveFormsModule,
        UtilsModule,
        DropdownComponent,
        FormSectionComponent,
        ButtonComponent,
        SvgIconComponent,
        TelInputComponent,
        RouterModule,
        SanitizeContactSearchDirective,
        ClientOffersSearchesProjectsComponent,
        SmallCompanyLogoPipe,
    ],
    templateUrl: './client-add-popup-form.component.html',
    styleUrl: './client-add-popup-form.component.less',
})
class ClientAddPopupFormComponent extends AppComponentBase implements OnInit, AfterViewInit, OnChanges {

    @ViewChild('searchFieldWrapper') searchFieldWrapper?: ElementRef;

    @Input() showPopupSpacer = true;

    @Input() showAddToMyClientsButton = true;

    @Input() autoContinueOnOwnContact = false;

    @Input() disableCreateForm = false;

    @Input() showMatchedClientSource = false;

    @Input() formData: any = null;

    @Input() sourcePrefill: nullable<ISourceSelection> = null;

    @Input() showSurveyActions: boolean = false;

    @Input() confirmMatchClear: boolean = false;

    @Output() onSubmit: EventEmitter<number> = new EventEmitter<number>();

    @Output() onClientSelected: EventEmitter<ClientMatchDetailsResponseModel> = new EventEmitter<ClientMatchDetailsResponseModel>();

    // Fires only when the contact had no match and was created by this form.
    @Output() onClientCreated = new EventEmitter<number>();

    @Output() onNoMatch = new EventEmitter<void>();

    @Output() onMatchCleared = new EventEmitter<void>();

    @Output() onSourceSelected = new EventEmitter<ISourceSelection>();

    @Output() surveyActionClicked = new EventEmitter<ISearchInterestSurveyAction>();

    clientForm?: FormGroup;

    sourceCategoriesOptions: IDropdownOption[] = [];

    subSourceCategoriesOptions: IDropdownOption[] = [];

    sourceDetailOptions: IDropdownOption[] = [];

    matchedClient?: ClientMatchDetailsResponseModel = null;

    isClientSearched = false;

    isInitialFormSubscription = true;

    nationalitiesOptions: IDropdownOption[] = [];

    offers: OfferOfClientResponseModel[] = [];

    searches: SearchOfClientResponseModel[] = [];

    projects: ProjectOfClientResponseModel[] = [];

    otherCompaniesActivity: ClientCrossCompanyActivityResponseModel[] = [];

    protected readonly AppPathConsts = AppPathConsts;

    protected readonly ContactDetailId = ContactDetailId;

    private dialogData: any = null;

    private destroyRef = inject(DestroyRef);

    private readonly minPhoneSearchTermLength = 6;

    private lastSearchedTerm: nullable<string> = null;

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private clientsService: ClientsServiceProxy,
        private sourceCategoriesService: SourceCategoriesService,
        private clientsFeaturesService: ClientsService,
        private nationalitiesServiceProxy: NationalitiesServiceProxy,
        private offersService: OffersServiceProxy,
        private searchesService: SearchesServiceProxy,
        private projectsService: ProjectsServiceProxy,
        private clientsCrossCompanyService: ClientsCrossCompanyServiceProxy,
        @Optional() @Inject(MAT_DIALOG_DATA) dialogData: any,
    ) {
        super(injector);
        this.dialogData = dialogData;
    }

    get data(): any {
        return this.formData || this.dialogData;
    }

    get showSearchField(): boolean {
        return !isNil(this.matchedClient) || !this.isClientSearched;
    }

    get showAddClientForm(): boolean {
        return isNil(this.matchedClient) && this.isClientSearched;
    }

    get showClientMatch(): boolean {
        return !isNil(this.matchedClient) && this.isClientSearched;
    }

    get showSources(): boolean {
        return this.isClientSearched && !this.matchedClient?.isRelatedWithMe;
    }

    get enableCheckButton(): boolean {
        const searchTerm = this.sanitizedSearchTerm;

        return isEmailSearchTerm(searchTerm) || searchTerm.length >= this.minPhoneSearchTermLength;
    }

    get contactDetailsFormArray() {
        return this.clientForm?.get('contactDetails') as FormArray;
    }

    get contactDetailsControls() {
        return this.contactDetailsFormArray.controls.filter((cd) =>
            cd.value.contactDetailId === ContactDetailId.Phone || cd.value.contactDetailId === ContactDetailId.Email);
    }

    get phoneNumberOrEmail(): string | undefined {
        return this.phoneNumberOrEmailControl?.value?.trim();
    }

    get sanitizedSearchTerm(): string {
        return sanitizeContactSearchTerm(this.phoneNumberOrEmail);
    }

    get firstNameControl(): AbstractControl {
        return this.clientForm?.get('firstName');
    }

    get lastNameControl(): AbstractControl {
        return this.clientForm?.get('lastName');
    }

    get firstName(): string {
        return this.firstNameControl?.value;
    }

    get lastName(): string {
        return this.lastNameControl?.value;
    }

    get email(): string {
        return this.contactDetailsFormArray.value.filter((cd) => cd.contactDetailId === ContactDetailId.Email)?.[0]?.value ?? '';
    }

    get phoneNumber(): string {
        return this.contactDetailsFormArray.value.filter((cd) => cd.contactDetailId === ContactDetailId.Phone)?.[0]?.value ?? '';
    }

    get sourceDetailTypeNumber(): number | undefined {
        return this.clientForm?.get('subSource')?.value?.detailType;
    }

    get sourceCategory() {
        return this.clientForm?.get('source');
    }

    get sourceCategoryId() {
        return this.sourceCategory?.value?.key;
    }

    get subSourceCategory() {
        return this.clientForm?.get('subSource');
    }

    get subSourceCategoryId() {
        return this.subSourceCategory?.value?.key;
    }

    get sourceDetail() {
        return this.clientForm?.get('sourceDetail');
    }

    get sourceDetailId() {
        return this.sourceDetail?.value?.key;
    }

    get matchedClientName(): string {
        return !isEmpty(this.matchedClient?.name)
            ? this.matchedClient.name
            : `${this.matchedClient?.firstName} ${this.matchedClient?.lastName}`;
    }

    get showSourceDetail(): boolean {
        return this.subSourceCategoryId && this.sourceDetailTypeNumber !== 0 && !isNil(this.sourceDetailTypeNumber);
    }

    get withMatchedClientButtonText(): string {
        return this.matchedClient.isRelatedWithMe
            ? this.l('Continue')
            : this.l('AddToMyClients');
    }

    get shouldDisableWithMatchedClientButton() {
        return !this.matchedClient?.isRelatedWithMe && this.isSourceMissing;
    }

    get isSourceMissing(): boolean {
        return isNil(this.sourceCategoryId) || isNil(this.subSourceCategoryId) || (this.showSourceDetail && isNil(this.sourceDetailId));
    }

    get sourceDetailPlaceholder(): string {
        return this.sourceCategoriesService.getSourceDetailPlaceholder(this.sourceDetailTypeNumber);
    }

    get matchedClientOwnSource(): nullable<ClientMatchSourceCategoryResponseModel> {
        return this.matchedClient?.clientsSourceCategories?.find((c) => c.employeeUserId === this.appSession.userId);
    }

    get matchedClientSourceDetailName() {
        const sourceCategory = this.matchedClientOwnSource;

        if (isNil(sourceCategory)) {
            return '';
        }

        const { sourceDetails } = sourceCategory;
        const sourceDetail = first(sourceDetails);

        if (isNil(sourceDetail)) {
            return '';
        }

        return getSourceDetailName(sourceDetail);
    }

    get matchedClientSource() {
        const clientSource = this.matchedClientOwnSource;

        if (isNil(clientSource)) {
            return '';
        }

        const { sourceCategoryParentName, sourceCategoryName } = clientSource;

        return [sourceCategoryParentName, sourceCategoryName, this.matchedClientSourceDetailName]
            .filter(x => !isNil(x) && !isEmpty(x))
            .join(', ');
    }

    get createRequestModel(): ClientRequestModel {
        const sourceDetail = this.clientsFeaturesService.extractSourceDetailValue(
            this.subSourceCategoryId,
            this.sourceDetailId,
            this.sourceDetailTypeNumber,
        );
        return {
            personalData: {
                firstName: this.firstName,
                lastName: this.lastName,
            },
            nationalityId: this.clientForm.get('nationality').value?.key,
            contactDetails: this.contactDetailsFormArray.value.filter((cd) => !isEmpty(cd.value)),
            clientsSourceCategories: [{
                sourceCategoryId: parseInt(this.subSourceCategoryId),
                sourceDetails: !isNil(sourceDetail)
                    ? [sourceDetail]
                    : [] as ClientSourceDetailRequestModel[],
            }] as ClientSourceCategoryRequestModel[],
        } as ClientRequestModel;
    }

    private get phoneNumberOrEmailControl(): nullable<AbstractControl> {
        return this.clientForm?.get('phoneNumberOrEmail');
    }

    ngAfterViewInit(): void {
        this.focusSearchField();
    }

    ngOnInit(): void {
        this.filterSourceCategories();

        this.filterNationalities();

        this.initializeForm(this.data?.clientPhone || '');

        this.applySourcePrefill();

        if (this.data?.skipSearch) {
            this.isClientSearched = true;
            this.matchedClient = null;
        } else {
            this.checkAndFetchClient();
        }
    }

    reset(): void {
        this.matchedClient = null;
        this.isClientSearched = false;
        this.isInitialFormSubscription = true;
        this.sourceCategoriesOptions = [];
        this.subSourceCategoriesOptions = [];
        this.sourceDetailOptions = [];

        this.resetClientActivity();
        this.initializeForm('');
        this.filterSourceCategories();

        afterNextRender(() => this.focusSearchField(), { injector: this.injector });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.formData && !changes.formData.firstChange && this.clientForm) {
            const phoneOrEmail = this.data?.clientPhone?.trim();
            if (!isEmpty(phoneOrEmail) && !this.isClientSearched) {
                const currentValue = this.phoneNumberOrEmailControl?.value;
                if (currentValue !== phoneOrEmail) {
                    this.phoneNumberOrEmailControl?.setValue(phoneOrEmail);
                }
                this.checkAndFetchClient();
            }
        }

        if (changes.sourcePrefill && !isNil(this.sourcePrefill) && this.clientForm) {
            this.applySourcePrefill();
        }
    }

    checkAndFetchClient(): void {
        const phoneOrEmail = this.data?.clientPhone?.trim();
        if (!isEmpty(phoneOrEmail) && !this.isClientSearched) {
            this.fetchClient();
        }
    }

    subscribeToFormChanges(): void {
        const phone = this.contactDetailsFormArray.controls
            .find((cd) => cd.get('contactDetailId').value === ContactDetailId.Phone)
            ?.get('value');

        const email = this.contactDetailsFormArray.controls
            .find((cd) => cd.get('contactDetailId').value === ContactDetailId.Email)
            ?.get('value');

        phone?.valueChanges.subscribe(() => {
            this.handlePhoneAndEmailValidation(email, phone);
        });

        email?.valueChanges.subscribe(() => {
            this.handlePhoneAndEmailValidation(email, phone);
        });

        if (this.isInitialFormSubscription) {
            this.handlePhoneAndEmailValidation(email, phone);
            this.isInitialFormSubscription = false;
        }

        this.sourceCategory?.valueChanges?.subscribe(() => {
            this.subSourceCategoriesOptions = [];
            this.subSourceCategory?.setValue(null);
            this.sourceDetail?.setValue(null);
        });

        this.subSourceCategory?.valueChanges?.subscribe(() => {
            this.sourceDetail?.setValue(null);
            this.sourceDetailOptions = [];
            this.sourceCategoriesService.updateSourceDetailRequiredValidator(
                this.sourceDetail,
                this.showSourceDetail,
                this.sourceDetailTypeNumber,
                { emitEvent: false },
            );
        });

        email.updateValueAndValidity();
        phone.updateValueAndValidity();
    }

    onPhoneDialCodeChange(): void {
        this.resolveNationality();
    }

    filterNationalities(): void {
        this.nationalitiesServiceProxy.getAll({ pageSize: 1000 } as NomenclaturesPaginatedRequestModel)
            .subscribe(({ items }) => {
                this.nationalitiesOptions = items?.map(defaultDropdownMapper) ?? [];
                this.resolveNationality();
            });
    }

    handlePhoneAndEmailValidation(emailControl: AbstractControl, phoneControl: AbstractControl) {
        const emailValue = emailControl?.value;
        const phoneValue = phoneControl?.value;

        if (isEmpty(emailValue) && isEmpty(phoneValue)) {
            emailControl.setValidators([Validators.required, Validators.email]);
            phoneControl.setValidators([Validators.required, Validators.minLength(6)]);
        } else {
            phoneControl.removeValidators(Validators.required);
            emailControl.removeValidators(Validators.required);
        }

        emailControl.updateValueAndValidity({ emitEvent: false });
        phoneControl.updateValueAndValidity({ emitEvent: false });
    }

    clearMatchedClient() {
        if (!this.confirmMatchClear) {
            this.resetMatchedClient();
            return;
        }

        this.message.confirm(this.l('CallCenterClearLoadedClientConfirmation'), this.l('AreYouSure'), (isConfirmed: boolean) => {
            if (isConfirmed) {
                this.resetMatchedClient();
                return;
            }

            this.phoneNumberOrEmailControl?.setValue(this.lastSearchedTerm);
        });
    }

    fetchClient() {
        if (isNil(this.phoneNumberOrEmail)) {
            return;
        }

        this.lastSearchedTerm = this.phoneNumberOrEmail;

        const searchValue = normalizePhoneNumber(this.sanitizedSearchTerm);

        this.otherCompaniesActivity = [];
        this.fetchOtherCompaniesActivity(searchValue);

        this.clientsService.getMatchDetailsByPhoneOrEmail(searchValue)
            .subscribe((response) => {
                this.isClientSearched = true;

                if (!isNil(response?.id)) {
                    this.matchedClient = response;

                    this.fetchClientActivity(this.matchedClient.id);

                    if (this.matchedClient.isRelatedWithMe && this.autoContinueOnOwnContact) {
                        this.emitMatchedClientSource();
                        this.onClientSelected.emit(this.matchedClient);
                        this.onSubmit.emit(this.matchedClient.id);
                    }
                } else {
                    this.resetClientActivity();

                    const isEmailAddress = isEmailSearchTerm(searchValue);
                    this.contactDetailsFormArray?.setValue(Object.keys(ContactDetailId)
                        .filter((k) => isNaN(parseInt(k)))
                        .map((key) => ({
                            contactDetailId: ContactDetailId[key],
                            value:
                                ContactDetailId[key] === ContactDetailId.Phone && !isEmailAddress
                                    ? searchValue
                                    : ContactDetailId[key] === ContactDetailId.Email && isEmailAddress
                                        ? searchValue
                                        : '',
                            isDefault: false,
                        })));
                    this.onNoMatch.emit();
                }
            });
    }

    filterSourceCategories(name?: string): void {
        this.sourceCategoriesService
            .getRootSources(name)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((options) => {
                this.sourceCategoriesOptions = options;
            });
    }

    onSourceCategoryChanged(event: { originalEvent: PointerEvent; value: IDropdownOption }) {
        this.sourceCategory?.setValue(event.value);
    }

    onSubSourceCategoryChanged(event: { originalEvent: PointerEvent; value: IDropdownOption }) {
        this.subSourceCategory?.setValue(event.value);
    }

    filterSubSourceCategories(): void {
        this.sourceCategoriesService
            .getSubSources(this.sourceCategoryId)
            .subscribe((options) => {
                this.subSourceCategoriesOptions = options;
            });
    }

    filterSourceDetails(searchString?: string): void {
        if (isNil(this.sourceDetailTypeNumber)) {
            return;
        }

        this.sourceCategoriesService
            .getSourceDetails(this.sourceDetailTypeNumber, searchString)
            .subscribe((options) => {
                this.sourceDetailOptions = options;
            });
    }

    addToMyClients() {
        if (!this.hasTenant) {
            this.notify.error(this.l('NoTenantErrorMessage'));
            return;
        }

        if (isNil(this.matchedClient)) {
            return;
        }

        const { isRelatedWithMe } = this.matchedClient;

        if (isRelatedWithMe) {
            this.emitMatchedClientSource();
            this.onSubmit.emit(this.matchedClient.id);
            this.onClientSelected.emit(this.matchedClient);
            return;
        }

        const sourceDetail = this.clientsFeaturesService.extractSourceDetailValue(
            this.subSourceCategoryId,
            this.sourceDetailId,
            this.sourceDetailTypeNumber,
        );

        const request = {
            sourceCategory: {
                clientId: this.matchedClient.id,
                sourceCategoryId: parseInt(this.subSourceCategoryId),
                sourceDetails: !isNil(sourceDetail)
                    ? [sourceDetail]
                    : [] as ClientSourceDetailRequestModel[],
            } as ClientSourceCategoryRequestModel,
        } as ClientSourcesRequestModel;

        this.clientsService.addClientSources(request)
            .subscribe({
                next: () => {
                    this.emitSelectedSource();
                    this.onSubmit.emit(this.matchedClient.id);
                    this.onClientSelected.emit(this.matchedClient);
                },
                error: () => {
                    this.notify.error(this.l('ContactAdmin'));
                },
            });
    }

    createClient() {
        if (!this.hasTenant) {
            this.notify.error(this.l('NoTenantErrorMessage'));
            return;
        }

        const phonesOrEmails = this.contactDetailsFormArray.controls
            .filter((cd) =>
                (cd.value.contactDetailId === ContactDetailId.Phone ||
                    cd.value.contactDetailId === ContactDetailId.Email) &&
                !isEmpty(cd.value.value))
            .map((cd) => cd.value.value as string);

        this.clientsService
            .getPhoneOrEmailExistence(phonesOrEmails, 0)
            .pipe(switchMap((response: ContactDetailsExistenceResponseModel[]) => {
                if (!isEmpty(response)) {
                    const result = this.clientsFeaturesService.onPhoneOrEmailExists(
                        response,
                        this.contactDetailsFormArray,
                    );

                    if (result) {
                        this.notify.error(this.l(
                            result.messageName,
                            result.message,
                            result.title,
                            result.options.clientId,
                        ));
                    }

                    return EMPTY;
                }

                return this.clientsService.create(this.createRequestModel);
            }))
            .subscribe({
                next: (id) => {
                    if (id) {
                        this.emitSelectedSource();
                        this.onClientCreated.emit(id);
                        this.onSubmit.emit(id);
                        this.onClientSelected.emit({
                            id,
                            firstName: this.firstName,
                            lastName: this.lastName,
                            email: this.email,
                            phoneNumber: this.phoneNumber,
                            isRelatedWithMe: false,
                        } as ClientMatchDetailsResponseModel);
                    }
                },
                error: () => this.notify.error(this.l('ContactAdmin')),
            });
    }

    private fetchClientActivity(clientId: number): void {
        this.spinnerService.show();

        const offers$ = this.getIfGranted(AppPermissions.OfferRead, this.offersService.getForClient(clientId));
        const searches$ = this.getIfGranted(AppPermissions.SearchRead, this.searchesService.getForClient(clientId));
        const projects$ = this.getIfGranted(AppPermissions.ProjectRead, this.projectsService.getActiveForClient(clientId));

        forkJoin({ offers$, searches$, projects$ })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.spinnerService.hide()),
            )
            .subscribe(({ offers$: offers, searches$: searches, projects$: projects }) => {
                this.offers = offers;
                this.searches = searches;
                this.projects = projects;
            });
    }

    private getIfGranted<T>(readPermission: string, request$: Observable<T[]>): Observable<T[]> {
        return this.isGrantedAny(readPermission, AppPermissions.AdministrationFullAccess)
            ? request$.pipe(catchError(() => this.handleError()))
            : of([]);
    }

    private fetchOtherCompaniesActivity(phoneNumberOrEmail: string): void {
        const crossCompanyLookupSetting = this.s(AppSettings.AppUiManagementMultiCompanyCrossCompanyClientLookupTenantIds);
        const crossCompanyLookupTenantIds: number[] = JSON.parse(crossCompanyLookupSetting ?? '[]');

        if (isEmpty(crossCompanyLookupTenantIds)) {
            return;
        }

        this.clientsCrossCompanyService.getActivityByPhoneOrEmail(phoneNumberOrEmail)
            .pipe(
                catchError(() => this.handleError()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((groups) => {
                this.otherCompaniesActivity = groups;
            });
    }

    private handleError() {
        this.notify.error(this.l('ContactAdmin'));
        return of([]);
    }

    private resetClientActivity(): void {
        this.offers = [];
        this.searches = [];
        this.projects = [];
    }

    private resetMatchedClient(): void {
        this.matchedClient = null;
        this.isClientSearched = false;
        this.resetClientActivity();
        this.otherCompaniesActivity = [];
        this.onMatchCleared.emit();
    }

    private initializeForm(seedPhone: string): void {
        this.clientForm = this.formBuilder.group({
            phoneNumberOrEmail: [
                seedPhone,
                this.data?.skipSearch
                    ? []
                    : [Validators.required],
            ],
            firstName: ['', [Validators.required, Validators.minLength(2), clientFirstNameValidator(), Validators.maxLength(100)]],
            lastName: ['', [Validators.required, Validators.minLength(2), clientFirstNameValidator(), Validators.maxLength(100)]],
            source: [null, Validators.required],
            subSource: [null, Validators.required],
            sourceDetail: [null],
            nationality: [null],
            contactDetails: this.formBuilder.array(Object
                .keys(ContactDetailId)
                .filter(k => isNaN(parseInt(k)))
                .map(key => this.formBuilder.group({
                    contactDetailId: [ContactDetailId[key], Validators.required],
                    value: [
                        ContactDetailId[key] === ContactDetailId.Phone
                            ? seedPhone
                            : '',
                        ContactDetailId[key] === ContactDetailId.Phone
                            ? Validators.minLength(10)
                            : ContactDetailId[key] === ContactDetailId.Email
                                ? Validators.email
                                : null,
                    ],
                    isDefault: [false, Validators.required],
                }))),
        });

        this.subscribeToFormChanges();
    }

    private focusSearchField(): void {
        const inputEl = this.searchFieldWrapper?.nativeElement?.querySelector('input') as HTMLElement;
        inputEl?.focus();
    }

    private resolveNationality(): void {
        if (isNil(this.clientForm)) {
            return;
        }
        this.clientsFeaturesService.updateNationalityFromPhone(
            this.clientForm,
            this.nationalitiesOptions,
            this.clientsFeaturesService.getFirstPhoneValue(this.contactDetailsFormArray),
        );
    }

    private applySourcePrefill(): void {
        if (isNil(this.sourcePrefill) || isNil(this.clientForm)) {
            return;
        }

        this.sourceCategoriesService.applySourceSelection(this.clientForm, this.sourcePrefill);

        if (!isNil(this.sourcePrefill.sourceDetail)) {
            this.sourceDetailOptions = [this.sourcePrefill.sourceDetail];
        }
    }

    private emitSelectedSource(): void {
        if (isNil(this.sourceCategoryId) || isNil(this.subSourceCategoryId)) {
            return;
        }

        this.onSourceSelected.emit({
            source: this.sourceCategory?.value,
            subSource: this.subSourceCategory?.value,
            sourceDetail: this.sourceDetail?.value,
        });
    }

    private emitMatchedClientSource(): void {
        const clientSource = this.matchedClientOwnSource;
        if (!isNil(this.sourcePrefill) || isNil(clientSource)) {
            return;
        }

        this.onSourceSelected.emit(this.sourceCategoriesService.toClientSourceSelection(clientSource));
    }
}

export default ClientAddPopupFormComponent;
